import { useMutation } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

// Path helpers
export const bizyChatV2Path = (prefix = '') => `${prefix}/bizy/chat/v2`;
export const bizyFeedbackPath = (messageId: string, prefix = '') => `${prefix}/bizy/feedback/${messageId}`;

// Streaming API Types
export interface BizyChatV2Request {
	message: string;
	conversation_id?: string;
	context?: Record<string, string>;
}

export interface BizyChatV2Response {
	success: boolean;
	conversation_id: string;
}

/**
 * Hook for sending chat messages to Bizy (streaming).
 *
 * POSTs to /api/v2/bizy/chat/v2 which enqueues a background job and returns
 * 202 immediately. The response is streamed via ActionCable (BizyChannel).
 */
export const useBizyChatV2Mutation = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (params: BizyChatV2Request): Promise<BizyChatV2Response> => {
			const response = await http.post<BizyChatV2Response>(bizyChatV2Path(), params);
			return response.data;
		},
	});
};

// Feedback API Types
export interface BizyFeedbackRequest {
	messageId: string;
	feedback_type: 'positive' | 'negative' | null;
	feedback_category?: string;
	feedback_details?: string;
}

export interface BizyFeedbackResponse {
	success: boolean;
	error?: string;
}

/**
 * Hook for submitting feedback on a Bizy response.
 *
 * PATCH /api/v2/bizy/feedback/:id
 */
export const useBizyFeedbackMutation = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async ({ messageId, ...data }: BizyFeedbackRequest): Promise<BizyFeedbackResponse> => {
			const response = await http.patch<BizyFeedbackResponse>(bizyFeedbackPath(messageId), data);
			return response.data;
		},
	});
};
